import PhoneField from '@components/Form/PhoneField';

import type { OrderingEligibilityFormikProps } from '../../OrderCheckEligibility/OrderCheckEligibility.validation';
import { CheckboxField } from '../common/CheckboxField';
import InputField from '../common/InputField';

interface GuardianFormFieldsProps {
  formik: OrderingEligibilityFormikProps;
  onNotGuardianClick: () => void;
  patientFirstName: string;
}

export function GuardianFormFields({ formik, onNotGuardianClick, patientFirstName }: GuardianFormFieldsProps) {
  const name = patientFirstName.trim() || 'the patient';
  const relationshipName = patientFirstName.trim() || 'patient';
  const legalAuthorityTouched = formik.touched.guardian?.legalAuthorityConfirmed;
  const legalAuthorityError = formik.errors.guardian?.legalAuthorityConfirmed;

  return (
    <div className='order-check-eligibility__guardian-section field-span-2'>
      <hr className='mt-3 mb-5' />
      <h2 className='order-check-eligibility__guardian-title'>Parent or legal guardian</h2>

      <p className='mb-4'>
        {`Because ${name} is under 18, we need information for the parent or legal guardian who will consent on ${name}'s behalf.`}
      </p>

      <div className='form-grid'>
        <div className='field'>
          <InputField
            id='guardian.firstName'
            label="Parent's / Guardian's first name"
            required
            placeholder="Parent's / Guardian's first name"
            value={formik.values.guardian.firstName}
            onChange={val => formik.setFieldValue('guardian.firstName', val)}
            onBlur={() => formik.setFieldTouched('guardian.firstName', true)}
            showError={formik.touched.guardian?.firstName}
            error={formik.errors.guardian?.firstName}
            phiSanitize
          />
        </div>

        <div className='field'>
          <InputField
            id='guardian.lastName'
            label="Parent's / Guardian's last name"
            required
            placeholder="Parent's / Guardian's last name"
            value={formik.values.guardian.lastName}
            onChange={val => formik.setFieldValue('guardian.lastName', val)}
            onBlur={() => formik.setFieldTouched('guardian.lastName', true)}
            showError={formik.touched.guardian?.lastName}
            error={formik.errors.guardian?.lastName}
            phiSanitize
          />
        </div>

        <div className='field'>
          <InputField
            id='guardian.dob'
            label="Parent's / Guardian's date of birth"
            required
            type='date'
            autoComplete='bday'
            value={formik.values.guardian.dob}
            onChange={val => formik.setFieldValue('guardian.dob', val)}
            onBlur={() => formik.setFieldTouched('guardian.dob', true)}
            showError={formik.touched.guardian?.dob}
            error={formik.errors.guardian?.dob}
            phiHidden
          />
        </div>

        <div className='field'>
          <InputField
            id='guardian.relationship'
            label={`Relationship to ${relationshipName}`}
            required
            placeholder='Parent, Legal guardian, etc.'
            value={formik.values.guardian.relationship}
            onChange={val => formik.setFieldValue('guardian.relationship', val)}
            onBlur={() => formik.setFieldTouched('guardian.relationship', true)}
            showError={formik.touched.guardian?.relationship}
            error={formik.errors.guardian?.relationship}
            phiSanitize
          />
        </div>

        <div className='field'>
          <InputField
            id='guardian.email'
            label="Parent's / Guardian's email"
            required
            type='email'
            placeholder='name@example.com'
            value={formik.values.guardian.email}
            onChange={val => formik.setFieldValue('guardian.email', val)}
            onBlur={() => formik.setFieldTouched('guardian.email', true)}
            showError={formik.touched.guardian?.email}
            error={formik.errors.guardian?.email}
            phiSanitize
          />
        </div>

        <div className='field'>
          <PhoneField
            name='guardian.phone'
            label="Parent's / Guardian's phone"
            required
            helperText="Parent's / Guardian's phone"
            phiSanitize
          />
        </div>

        <div className='field field-span-2 order-check-eligibility__callout'>
          <CheckboxField
            id='guardian.legalAuthorityConfirmed'
            label={<strong>{`I confirm I have legal authority to make medical decisions for ${name}.`}</strong>}
            description={
              <span className='order-check-eligibility__callout-helper'>
                {`By checking this, you're attesting that you are ${name}'s parent or court-appointed legal guardian. Required to proceed.`}
              </span>
            }
            required
            checked={formik.values.guardian.legalAuthorityConfirmed}
            onChange={val => formik.setFieldValue('guardian.legalAuthorityConfirmed', val)}
            showError={!!(legalAuthorityTouched && legalAuthorityError)}
            error={legalAuthorityError as string}
          />
        </div>
      </div>

      <button type='button' className='order-check-eligibility__not-guardian-link' onClick={onNotGuardianClick}>
        {`I'm not ${name}'s legal guardian`}
      </button>
    </div>
  );
}
